import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { ApolloProvider } from '@/apollo';
import { Toaster } from 'sonner';

import App from './App';
import { apolloClient } from './services/graphql';
import { checkServerHealth, checkServerAvailable } from './utils/healthCheck';
import { ServerStarting } from './components/ServerStarting';
import './index.css';

// GLOBALLY override window.confirm to prevent browser dialogs
// This must happen BEFORE any components render
// This blocks ALL browser confirm() dialogs - components must use custom modals instead
// Using Object.defineProperty to make it non-writable and non-configurable
Object.defineProperty(window, 'confirm', {
  value: function(_message?: string): boolean {
    // [BLOCKED] - Always return false to prevent any confirmations
    // Components should use custom modals instead
    return false;
  },
  writable: false,
  configurable: false
});

// Clear any cached applicationSettings to fix persistence issue
try {
  const apolloCacheData = localStorage.getItem('apollo-cache-persist');
  if (apolloCacheData) {
    const cacheObj = JSON.parse(apolloCacheData);
    if (cacheObj?.ROOT_QUERY?.applicationSettings) {
      delete cacheObj.ROOT_QUERY.applicationSettings;
      localStorage.setItem('apollo-cache-persist', JSON.stringify(cacheObj));
    }
  }
} catch (error) {
  // Silent cleanup failure
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes
      retry: (failureCount, error: any) => {
        if (error?.status === 404) return false;
        return failureCount < 3;
      },
    },
    mutations: {
      retry: (failureCount, error: any) => {
        if (error?.status === 404) return false;
        return failureCount < 3;
      },
    },
  },
});

/**
 * Poll server health check until server responds
 * @param intervalMs - Interval between checks in milliseconds (default: 2000)
 * @param maxAttempts - Maximum number of attempts (default: Infinity)
 * @returns Promise that resolves when server is healthy
 */
async function waitForServer(intervalMs: number = 2000, maxAttempts: number = Infinity): Promise<void> {
  let attempts = 0;
  
  while (attempts < maxAttempts) {
    attempts++;
    
    // First check if server is available at all (faster check)
    const serverAvailable = await checkServerAvailable();
    
    if (serverAvailable) {
      // Server is responding, now check if GraphQL health is ready
      const result = await checkServerHealth();
      
      if (result.healthy) {
        console.log('Server is ready!');
        return;
      }
    }
    
    // Wait before next attempt
    await new Promise(resolve => setTimeout(resolve, intervalMs));
  }
  
  throw new Error('Server did not become available within the maximum number of attempts');
}

/**
 * Initialize and render the application
 */
async function initializeApp() {
  try {
    const rootElement = document.getElementById('root');
    
    if (!rootElement) {
      throw new Error('Root element not found');
    }
    
    const root = ReactDOM.createRoot(rootElement);
    
    // First, quickly check if server is already ready
    const serverAvailable = await checkServerAvailable();
    
    let serverReady = false;
    if (serverAvailable) {
      // Server is responding, check if GraphQL health is ready
      const result = await checkServerHealth();
      serverReady = result.healthy;
    }
    
    // Only show server starting screen if server is NOT ready
    if (!serverReady) {
    root.render(
      <React.StrictMode>
        <ServerStarting />
      </React.StrictMode>
    );
    
    // Wait for server to be ready
    try {
      await waitForServer(2000); // Check every 2 seconds
    } catch (error) {
      console.error('Failed to connect to server:', error);
      // Continue anyway - let the app handle connection errors
      }
    }
    
    // Server is ready, render the actual app
    root.render(
      <React.StrictMode>
        <ApolloProvider client={apolloClient}>
          <QueryClientProvider client={queryClient}>
            <App />
            <Toaster
              position="top-right"
              richColors
              closeButton
              duration={4000}
            />
          </QueryClientProvider>
        </ApolloProvider>
      </React.StrictMode>
    );
  } catch (error) {
    console.error('main.tsx: Error during application startup:', error);
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.innerHTML = `
        <div style="padding: 20px; background: #fee; color: #c00; font-family: Arial;">
          <h1>Application Startup Error</h1>
          <p>Error: ${error}</p>
          <p>Check console for details</p>
        </div>
      `;
    }
  }
}

// Start the application
initializeApp();
